/**
 * Phase 66 Plan 02 — sticky 429 rate-limit countdown banner.
 *
 * Port of `js/admin/rate-limit-banner.js` (Phase 34 Plan 03 sticky
 * banner). Behavioral contract preserved:
 *   - Subscribes to `rateLimitStore`; renders null when `!active`.
 *   - When triggered, displays "Limite raggiunto: riprova tra Ns."
 *     with a live 1s countdown.
 *   - At 0, calls `rateLimitStore.clear()` and dismisses (T-66-02-07).
 *   - No dismiss-X button during the wait (UI-SPEC §Banner styling).
 *
 * Class-name contract preserved per 66-TESTID-CONTRACT.md (KEEP rows):
 *   .admin-rate-banner, .admin-rate-banner__icon,
 *   .admin-rate-banner__body, .admin-rate-banner__countdown
 *
 * Plus new test affordances:
 *   data-testid="rate-limit-banner"     (root)
 *   data-testid="rate-limit-countdown"  (countdown <strong>)
 */

import { useEffect, useRef, useSyncExternalStore } from "react";

import { rateLimitStore } from "@/stores/rateLimitStore";

function subscribe(listener: () => void) {
  return rateLimitStore.subscribe(listener);
}
function getSnapshot() {
  return rateLimitStore.getState();
}

export default function RateLimitBanner() {
  const state = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  const intervalRef = useRef<number | null>(null);

  useEffect(() => {
    if (!state.active) {
      // Make sure no stale interval is left running.
      if (intervalRef.current !== null) {
        window.clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    // Restart countdown from current retryAfter — store update => new effect run.
    if (intervalRef.current !== null) {
      window.clearInterval(intervalRef.current);
    }

    let remaining = state.retryAfter;
    intervalRef.current = window.setInterval(() => {
      remaining -= 1;
      if (remaining <= 0) {
        if (intervalRef.current !== null) {
          window.clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
        rateLimitStore.clear();
      } else {
        // Force re-render via store push so the displayed countdown updates.
        rateLimitStore.trigger(remaining);
      }
    }, 1000);

    return () => {
      if (intervalRef.current !== null) {
        window.clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
    // We intentionally re-run the effect when active flips, NOT on every
    // retryAfter tick (the trigger() inside setInterval would create a loop).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.active]);

  if (!state.active) return null;

  return (
    <div
      className="admin-rate-banner"
      role="alert"
      aria-live="assertive"
      data-testid="rate-limit-banner"
    >
      <span className="admin-rate-banner__icon" aria-hidden="true">
        {"⏲"}
      </span>
      <span className="admin-rate-banner__body">
        Limite raggiunto: riprova tra{" "}
        <strong
          className="admin-rate-banner__countdown"
          data-testid="rate-limit-countdown"
        >
          {state.retryAfter}
        </strong>
        s.
      </span>
    </div>
  );
}
